// ═══════════════════════════════════════════════════════════════════════════════
// AJAX CART - 4-LAYER ARCHITECTURE
// ═══════════════════════════════════════════════════════════════════════════════
//
// Architecture:
//   UI Layer → Operation Layer → State Layer → API Layer
//      ↓            ↓                ↓             ↓
//   DOM/UX    Business Logic    Cart Cache    Shopify API
//
// Key Features:
// - Request Queue (max 2 concurrent) prevents burst traffic
// - Debouncing (800ms qty, 500ms gift) prevents rapid-fire
// - Batch Updates (single API call for product + gifts)
// - Smart Caching (5-second cart state cache)
// - Parallel Section Refresh (Promise.all instead of sequential)
// - Retry with Exponential Backoff (auto-retry 429 errors)
//
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Debounce function - delays execution until after wait milliseconds have elapsed
 * since the last time the debounced function was invoked.
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
function debounce(fn, delay) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Sleep for specified milliseconds
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ═══════════════════════════════════════════════════════════════════════════════
// API LAYER - Shopify Cart API Wrapper with Queue & Retry
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Request Queue - Limits concurrent requests to prevent rate limiting
 */
class RequestQueue {
  constructor(maxConcurrent = 2) {
    this.maxConcurrent = maxConcurrent;
    this.running = 0;
    this.queue = [];
  }

  /**
   * Add a request to the queue
   * @param {Function} requestFn - Async function that returns a Promise
   * @returns {Promise<any>} Result of the request
   */
  async add(requestFn) {
    // If at capacity, queue the request
    if (this.running >= this.maxConcurrent) {
      await new Promise(resolve => this.queue.push(resolve));
    }

    this.running++;

    try {
      const result = await requestFn();
      return result;
    } finally {
      this.running--;

      // Process next item in queue
      if (this.queue.length > 0) {
        const resolve = this.queue.shift();
        resolve();
      }
    }
  }
}

// Global request queue instance
const requestQueue = new RequestQueue(2);

// Trusted-write signal, owned by assets/cart-observer.js (loaded first). Every
// apiLayer cart write brackets itself with beginTrusted/endTrusted so that, at
// `cart:changed` emit time, a theme write reads as trusted (depth > 0) and an
// external write (DevTools console, third-party app, second tab) reads as
// untrusted (depth 0). The observer uses this both to drive section refreshes
// for external changes AND to wipe the cart on console/DevTools tampering — so
// any cart write this theme makes legitimately MUST go through apiLayer below.
// Fallback no-ops keep ajax-cart working if the observer asset ever fails.
const cartTrust = window.RasasiCartObserver || {
  beginTrusted() {},
  endTrusted() {},
  isTrusted() {
    return false;
  }
};

/**
 * Fetch with automatic retry and exponential backoff
 * @param {string} url - URL to fetch
 * @param {object} options - Fetch options
 * @param {number} maxRetries - Maximum number of retries (default: 3)
 * @returns {Promise<Response>}
 */
async function fetchWithRetry(url, options = {}, maxRetries = 3) {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);

      // If we get a 429 (rate limit), retry with backoff
      if (response.status === 429 && attempt < maxRetries) {
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter
          ? parseInt(retryAfter) * 1000
          : Math.pow(2, attempt) * 1000; // Exponential backoff: 1s, 2s, 4s

        console.warn(`Rate limited (429). Retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
        await sleep(delay);
        continue;
      }

      return response;
    } catch (error) {
      lastError = error;

      // Retry network errors with backoff
      if (attempt < maxRetries) {
        const delay = Math.pow(2, attempt) * 1000;
        console.warn(`Network error. Retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
        await sleep(delay);
        continue;
      }
    }
  }

  throw lastError || new Error('Max retries exceeded');
}

/**
 * API Layer - Wrapped Shopify Cart API methods with queue and retry
 */
const apiLayer = {
  /**
   * Add item to cart
   * @param {string} variantId - Shopify variant ID
   * @param {number} quantity - Quantity to add
   * @param {object} properties - Line item properties
   * @returns {Promise<object>} Cart item data
   */
  async cartAdd(variantId, quantity, properties = {}) {
    return requestQueue.add(async () => {
      cartTrust.beginTrusted();
      let response;
      try {
        response = await fetchWithRetry('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity, properties })
        });
      } finally {
        cartTrust.endTrusted();
      }

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Failed to add to cart';

        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.description || errorData.message || errorMessage;
          } catch (parseError) {
            console.error('Failed to parse error JSON:', parseError);
          }
        } else {
          if (response.status === 404) {
            errorMessage = 'Product not found. Please ensure the gift products exist in Shopify.';
          } else if (response.status === 422) {
            errorMessage = 'Cannot add product to cart. It may be out of stock or unavailable.';
          } else {
            errorMessage = `Failed to add to cart (HTTP ${response.status})`;
          }
        }

        throw new Error(errorMessage);
      }

      return response.json();
    });
  },

  /**
   * Change line item quantity
   * @param {string} lineItemKey - Line item key
   * @param {number} quantity - New quantity
   * @returns {Promise<object>} Updated cart data
   */
  async cartChange(lineItemKey, quantity) {
    return requestQueue.add(async () => {
      cartTrust.beginTrusted();
      let response;
      try {
        response = await fetchWithRetry('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineItemKey, quantity })
        });
      } finally {
        cartTrust.endTrusted();
      }

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Failed to update cart';

        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          errorMessage = data.message || data.description || errorMessage;
        } else {
          errorMessage = `Failed to update cart (HTTP ${response.status})`;
        }

        throw new Error(errorMessage);
      }

      return response.json();
    });
  },

  /**
   * Batch update multiple line items
   * @param {object} updates - Object mapping line item keys to quantities
   * @returns {Promise<object>} Updated cart data
   */
  async cartUpdate(updates) {
    return requestQueue.add(async () => {
      cartTrust.beginTrusted();
      let response;
      try {
        response = await fetchWithRetry('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
      } finally {
        cartTrust.endTrusted();
      }

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Failed to update cart';

        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          errorMessage = data.message || data.description || errorMessage;
        } else {
          errorMessage = `Failed to update cart (HTTP ${response.status})`;
        }

        throw new Error(errorMessage);
      }

      return response.json();
    });
  },

  /**
   * Get current cart state (uses cache when available)
   * @param {boolean} forceRefresh - Force bypass cache
   * @returns {Promise<object>} Cart data
   */
  async cartGet(forceRefresh = false) {
    // Delegate to state layer's cached version
    return cartState.get(forceRefresh);
  },

  /**
   * Fetch multiple section renders in parallel
   * @param {string[]} sectionIds - Array of section IDs to fetch
   * @returns {Promise<object>} Map of section ID to HTML string
   */
  async sectionRender(sectionIds) {
    const fetchPromises = sectionIds.map(sectionId =>
      fetchWithRetry(`/?section_id=${sectionId}`)
        .then(res => res.text())
        .then(html => ({ sectionId, html }))
    );

    const results = await Promise.all(fetchPromises);

    // Convert array to map
    const htmlMap = {};
    results.forEach(({ sectionId, html }) => {
      htmlMap[sectionId] = html;
    });

    return htmlMap;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// STATE LAYER - Cart Cache & State Management
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Cart State Manager - Caches cart data to reduce API calls
 */
class CartState {
  constructor() {
    this.cart = null;
    this.lastFetch = 0;
    this.cacheDuration = 5000; // 5 seconds
    this.subscribers = [];
  }

  /**
   * Get cart state (from cache if fresh, otherwise fetch)
   * @param {boolean} forceRefresh - Force fetch from API
   * @returns {Promise<object>} Cart data
   */
  async get(forceRefresh = false) {
    const now = Date.now();
    const isCacheFresh = (now - this.lastFetch) < this.cacheDuration;

    if (!forceRefresh && this.cart && isCacheFresh) {
      console.log('Cart state: cache hit');
      return this.cart;
    }

    console.log('Cart state: fetching from API');

    // Fetch fresh cart data (not through queue, as it's a read operation)
    const response = await fetchWithRetry('/cart.js');
    if (!response.ok) {
      throw new Error(`Failed to fetch cart (HTTP ${response.status})`);
    }

    this.cart = await response.json();
    this.lastFetch = now;
    this.notifySubscribers(this.cart);

    return this.cart;
  }

  /**
   * Optimistically update cart state (instant UI feedback)
   * @param {string} lineItemKey - Line item key
   * @param {number} quantity - New quantity
   */
  updateOptimistic(lineItemKey, quantity) {
    if (!this.cart) return;

    const item = this.cart.items.find(i => i.key === lineItemKey);
    if (item) {
      item.quantity = quantity;
      this.notifySubscribers(this.cart);
    }
  }

  /**
   * Invalidate cache (force next get() to fetch)
   */
  invalidate() {
    this.lastFetch = 0;
    console.log('Cart state: cache invalidated');
  }

  /**
   * Subscribe to cart state changes
   * @param {Function} callback - Function to call when cart changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all subscribers of cart change
   * @param {object} cart - Updated cart data
   */
  notifySubscribers(cart) {
    this.subscribers.forEach(callback => callback(cart));
  }
}

// Global cart state instance
const cartState = new CartState();

/**
 * Find gift items for a specific product variant (uses cached cart)
 * @param {string} parentVariantId - Parent product variant ID
 * @returns {Promise<array>} Array of gift line items
 */
async function findGiftItems(parentVariantId) {
  const cart = await cartState.get();
  const parentVariantStr = String(parentVariantId);

  // Look up the SKU for this variant (gift boxes reference products by SKU)
  const parentItem = cart.items.find(i => String(i.variant_id) === parentVariantStr);
  const parentSku = parentItem?.sku || '';

  const giftItems = cart.items.filter(item => {
    if (!item.properties) return false;
    // Legacy gift option items
    if (item.properties._is_gift_option === 'true' && item.properties._for_variant === parentVariantStr) {
      return true;
    }
    // New gift box items (referenced by SKU)
    if (parentSku && item.properties._gift_box_type &&
        (item.properties._for_product_1 === parentSku || item.properties._for_product_2 === parentSku)) {
      return true;
    }
    return false;
  });

  console.log(`Found ${giftItems.length} gift items for variant ${parentVariantId}`);
  return giftItems;
}

/**
 * Get current gift type for a product (uses cached cart)
 * @param {string} parentVariantId - Parent product variant ID
 * @returns {Promise<string|null>} Gift type or null
 */
async function getCurrentGiftType(parentVariantId) {
  const giftItems = await findGiftItems(parentVariantId);

  if (giftItems.length === 0) return null;

  return giftItems[0].properties._gift_type || null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// OPERATION LAYER - Business Logic
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add product to cart
 * @param {string} variantId - Shopify variant ID
 * @param {number} quantity - Quantity to add
 * @param {object} properties - Line item properties
 * @returns {Promise<object>} Cart item data
 */
async function addToCart(variantId, quantity = 1, properties = {}) {
  try {
    console.log('Adding to cart:', { variantId, quantity, properties });

    if (!variantId) {
      throw new Error('Variant ID is required');
    }

    const data = await apiLayer.cartAdd(variantId, quantity, properties);

    // Invalidate cache after mutation
    cartState.invalidate();

    console.log('Added to cart successfully:', data);
    return data;
  } catch (error) {
    console.error('Add to cart failed:', error);
    const translations = document.getElementById('theme-translations');
    const errorMsg = error.message || translations?.dataset.somethingWentWrong || 'Something went wrong! Please try again.';
    alert(errorMsg);
    throw error;
  }
}

/**
 * Update product quantity (debounced 800ms, batches product + gifts)
 * @param {string} lineItemKey - Line item key
 * @param {number} newQty - New quantity
 * @param {string} variantId - Variant ID (for gift sync)
 * @returns {Promise<void>}
 */
async function updateQuantityOperation(lineItemKey, newQty, variantId) {
  // Show loading when operation actually executes (after debounce)
  uiLayer.showLoading(`item-${lineItemKey}`);

  try {
    console.log('Updating quantity:', { lineItemKey, newQty, variantId });

    // 1. Update DOM immediately (optimistic UI update)
    const qtyInputs = document.querySelectorAll(`.product-qty-input[data-item-key="${lineItemKey}"]`);
    qtyInputs.forEach(input => {
      input.value = newQty;
    });

    // 2. Find gift items from cache (0 API calls!)
    const giftItems = variantId ? await findGiftItems(variantId) : [];

    // 3. Batch update: product + legacy gift options in ONE request
    // Note: Gift boxes (_is_gift_box) are always qty 1, independent of product qty
    const updates = { [lineItemKey]: newQty };
    giftItems.forEach(gift => {
      if (gift.properties && gift.properties._gift_box_type) return; // skip gift boxes
      updates[gift.key] = newQty;
    });

    await apiLayer.cartUpdate(updates);

    // 4. Invalidate cache and refresh UI
    cartState.invalidate();
    await uiLayer.refreshSections(['quick-cart', 'cart', 'header']);

    // 5. Check free gift eligibility after quantity change
    await checkFreeGiftEligibility();

    console.log('Quantity updated successfully');
  } catch (error) {
    console.error('Quantity update failed:', error);
    uiLayer.showError(error.message || 'Failed to update quantity', `item-${lineItemKey}`);
    throw error;
  } finally {
    // Hide loading after operation completes
    uiLayer.hideLoading(`item-${lineItemKey}`);
  }
}

// Debounced version (800ms) - exposed to event handlers
const updateQuantity = debounce(updateQuantityOperation, 800);

/**
 * Change gift option (debounced 500ms, batches remove + add)
 * @param {string} parentVariantId - Parent product variant ID
 * @param {string} newGiftType - New gift type ('none', 'wrap', 'box')
 * @param {string|null} giftVariantId - Gift product variant ID
 * @param {number} quantity - Quantity to add
 * @param {string} productName - Parent product name
 * @param {HTMLElement} giftSelector - Gift selector element (for loading state)
 * @returns {Promise<void>}
 */
async function changeGiftOptionOperation(parentVariantId, newGiftType, giftVariantId, quantity, productName, giftSelector) {
  // Show loading when operation actually executes (after debounce)
  if (giftSelector) {
    giftSelector.style.opacity = '0.5';
    giftSelector.style.pointerEvents = 'none';
  }

  try {
    console.log('Changing gift option:', { parentVariantId, newGiftType, giftVariantId, quantity });

    // 1. Get existing gifts from cache (0 API calls!)
    const existingGifts = await findGiftItems(parentVariantId);

    // 2. Batch remove old gifts
    if (existingGifts.length > 0) {
      const updates = {};
      existingGifts.forEach(gift => {
        updates[gift.key] = 0;
      });

      await apiLayer.cartUpdate(updates);
      cartState.invalidate();
    }

    // 3. Add new gift (if not "none")
    if (newGiftType !== 'none' && giftVariantId) {
      const properties = {
        _for_variant: String(parentVariantId),
        _product_name: productName,
        _is_gift_option: 'true',
        _gift_type: newGiftType
      };

      await apiLayer.cartAdd(giftVariantId, quantity, properties);
      cartState.invalidate();
    }

    // 4. Single refresh
    await uiLayer.refreshSections(['quick-cart', 'cart', 'header']);

    console.log('Gift option changed successfully');
  } catch (error) {
    console.error('Gift option change failed:', error);
    alert(error.message);
    throw error;
  } finally {
    // Remove loading state after operation completes
    if (giftSelector) {
      giftSelector.style.opacity = '1';
      giftSelector.style.pointerEvents = 'auto';
    }
  }
}

// Debounced version (500ms) - exposed to event handlers
const changeGiftOption = debounce(changeGiftOptionOperation, 500);

/**
 * Remove cart item (batches product + gifts)
 * @param {string} lineItemKey - Line item key
 * @param {string|null} variantId - Variant ID (for gift removal)
 * @returns {Promise<void>}
 */
async function removeCartItem(lineItemKey, variantId = null) {
  try {
    console.log('Removing cart item:', { lineItemKey, variantId });

    // 1. Find gift items from cache
    const giftItems = variantId ? await findGiftItems(variantId) : [];

    // 2. Batch remove: product + gifts in ONE request
    // For gift boxes, remove the entire box (even if it references 2 products)
    const updates = { [lineItemKey]: 0 };
    giftItems.forEach(gift => {
      updates[gift.key] = 0;
    });

    await apiLayer.cartUpdate(updates);

    // 3. Invalidate and refresh
    cartState.invalidate();
    await uiLayer.refreshSections(['quick-cart', 'cart', 'header']);

    // 4. Check free gift eligibility after item removal
    await checkFreeGiftEligibility();

    console.log('Cart item removed successfully');
  } catch (error) {
    console.error('Remove cart item failed:', error);
    throw error;
  }
}

/**
 * Add gift option to cart
 * @param {string} parentVariantId - Parent product variant ID
 * @param {string} giftHandle - Gift handle ('wrap' or 'box')
 * @param {string} giftVariantId - Gift product variant ID
 * @param {number} quantity - Quantity
 * @param {string} productName - Parent product name
 * @returns {Promise<void>}
 */
async function addGiftOption(parentVariantId, giftHandle, giftVariantId, quantity, productName) {
  try {
    console.log('Adding gift option:', { parentVariantId, giftHandle, giftVariantId, quantity, productName });

    if (!giftVariantId) {
      throw new Error(`Gift variant ID is missing for ${giftHandle}. Please ensure the gift-${giftHandle} product exists in Shopify.`);
    }

    const properties = {
      _for_variant: String(parentVariantId),
      _product_name: productName,
      _is_gift_option: 'true',
      _gift_type: giftHandle
    };

    await addToCart(giftVariantId, quantity, properties);
  } catch (error) {
    console.error('Add gift option failed:', error);
    throw error;
  }
}

/**
 * Read the configured free gift threshold (in currency units, e.g. AED).
 * @returns {number}
 */
function getFreeGiftThreshold() {
  const translations = document.getElementById('theme-translations');
  const raw = parseFloat(translations?.dataset.freeGiftThreshold);
  return Number.isFinite(raw) && raw > 0 ? raw : 400;
}

/**
 * Compute the qualifying total (in currency units, e.g. AED) for the free gift
 * threshold. Uses cart.total_price (post-discount, post-cart-level-discount-codes)
 * minus the gift line's final_line_price so any actual discount applied at the
 * cart level reduces qualifying spend. This is intentionally stricter than the
 * old `original_line_price` sum, which counted catalog prices and let
 * discounted carts cross the threshold artificially.
 * @param {object} cart - Shopify cart object from /cart.js
 * @returns {{ qualifyingTotal: number, existingGift: object|null }}
 */
function computeFreeGiftQualifyingTotal(cart) {
  const existingGift = cart.items.find(
    item => item.properties && item.properties._is_free_gift === 'true'
  ) || null;

  let qualifyingCents = Number(cart.total_price) || 0;
  if (existingGift) {
    qualifyingCents -= Number(existingGift.final_line_price) || 0;
  }
  if (qualifyingCents < 0) qualifyingCents = 0;

  return { qualifyingTotal: qualifyingCents / 100, existingGift };
}

// Module-level busy flag prevents two near-simultaneous selectFreeGift() calls
// (e.g. user clicking two different "Select" buttons quickly) from inserting
// two free gift lines before the cart state reflects the first add.
let selectingFreeGift = false;

/**
 * Select a free gift (Discovery Set) - adds to cart with _is_free_gift property.
 * If a gift already exists, swaps it.
 * @param {string} variantId - Variant ID of the free gift product
 * @returns {Promise<void>}
 */
async function selectFreeGift(variantId) {
  if (selectingFreeGift) {
    console.warn('Free gift selection already in progress, ignoring duplicate click');
    return;
  }
  if (!variantId) {
    console.warn('selectFreeGift called without variantId');
    return;
  }

  selectingFreeGift = true;
  try {
    console.log('Selecting free gift:', { variantId });

    const cart = await cartState.get(true);
    const { qualifyingTotal, existingGift } = computeFreeGiftQualifyingTotal(cart);
    const threshold = getFreeGiftThreshold();

    // Safety net: refuse to add a gift if the cart is below threshold. The
    // picker UI already gates this, but the function is globally callable
    // (inline onclick, console, race conditions, stale cached HTML) so we
    // re-verify against fresh cart state here.
    if (qualifyingTotal < threshold) {
      console.warn('Free gift refused: qualifying total below threshold', {
        qualifyingTotal,
        threshold,
      });
      const translations = document.getElementById('theme-translations');
      const msg = translations?.dataset.freeGiftNotEligible
        || `Add more items to reach ${threshold} to qualify for a free gift.`;
      alert(msg);
      // Refresh the picker so its locked state matches reality.
      await uiLayer.refreshSections(['quick-cart', 'cart']);
      return;
    }

    // Remove existing gift if swapping
    if (existingGift) {
      await apiLayer.cartUpdate({ [existingGift.key]: 0 });
      cartState.invalidate();
    }

    // Add new gift
    await apiLayer.cartAdd(variantId, 1, { _is_free_gift: 'true' });
    cartState.invalidate();

    await uiLayer.refreshSections(['quick-cart', 'cart', 'header']);
    console.log('Free gift selected successfully');
  } catch (error) {
    console.error('Select free gift failed:', error);
    alert(error.message || 'Failed to add free gift');
  } finally {
    selectingFreeGift = false;
  }
}

/**
 * Remove a free gift from the cart
 * @param {string} lineItemKey - Line item key of the free gift
 * @returns {Promise<void>}
 */
async function removeFreeGift(lineItemKey) {
  try {
    console.log('Removing free gift:', { lineItemKey });

    await apiLayer.cartUpdate({ [lineItemKey]: 0 });
    cartState.invalidate();

    await uiLayer.refreshSections(['quick-cart', 'cart', 'header']);
    console.log('Free gift removed successfully');
  } catch (error) {
    console.error('Remove free gift failed:', error);
    alert(error.message || 'Failed to remove free gift');
  }
}

/**
 * Check free gift eligibility - auto-removes gift if cart drops below threshold
 * @returns {Promise<void>}
 */
async function checkFreeGiftEligibility() {
  try {
    const cart = await cartState.get(true);
    const { qualifyingTotal, existingGift } = computeFreeGiftQualifyingTotal(cart);

    if (!existingGift) return;

    const threshold = getFreeGiftThreshold();

    if (qualifyingTotal < threshold) {
      console.log('Cart below free gift threshold, removing gift', {
        qualifyingTotal,
        threshold,
      });
      await apiLayer.cartUpdate({ [existingGift.key]: 0 });
      cartState.invalidate();
      await uiLayer.refreshSections(['quick-cart', 'cart', 'header']);
    }
  } catch (error) {
    console.error('Free gift eligibility check failed:', error);
  }
}

/**
 * Cleanup orphaned gift items (gifts without parent products)
 * @returns {Promise<void>}
 */
async function cleanupOrphanedGiftItems() {
  try {
    const cart = await cartState.get(true); // Force fresh

    // Get regular cart items (exclude gift options and gift boxes)
    const regularItems = cart.items.filter(item => {
      if (!item.properties) return true;
      return item.properties._is_gift_option !== 'true' && !item.properties._gift_box_type;
    });

    // Variant IDs for legacy gift option orphan check
    const productVariantIds = new Set(regularItems.map(item => String(item.variant_id)));
    // SKUs for gift box orphan check
    const productSkus = new Set(regularItems.map(item => item.sku).filter(Boolean));

    // Find orphaned gift items
    const orphanedGifts = cart.items.filter(item => {
      if (!item.properties) return false;
      // Legacy gift options: orphaned if parent variant not in cart
      if (item.properties._is_gift_option === 'true') {
        return !productVariantIds.has(item.properties._for_variant);
      }
      // Gift boxes: orphaned if BOTH referenced SKUs are not in cart
      if (item.properties._gift_box_type) {
        const v1InCart = item.properties._for_product_1 && productSkus.has(item.properties._for_product_1);
        const v2InCart = item.properties._for_product_2 && productSkus.has(item.properties._for_product_2);
        return !v1InCart && !v2InCart;
      }
      return false;
    });

    // Batch remove orphaned gifts
    if (orphanedGifts.length > 0) {
      const updates = {};
      orphanedGifts.forEach(gift => {
        updates[gift.key] = 0;
      });

      await apiLayer.cartUpdate(updates);
      cartState.invalidate();
      await uiLayer.refreshSections(['quick-cart', 'cart', 'header']);

      console.log(`Cleaned up ${orphanedGifts.length} orphaned gift items`);
    }
  } catch (error) {
    console.error('Cleanup orphaned gifts failed:', error);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// CART INTEGRITY VALIDATOR
// ═══════════════════════════════════════════════════════════════════════════════
// Runs on every `cart:changed` event emitted by assets/cart-observer.js — i.e.
// on every successful /cart/(add|change|update|clear) request from any source
// (theme, third-party app, console, Buy Button). Catches violations that the
// per-operation checks miss when mutations originate outside ajax-cart.js.
//
// Violations enforced:
//   1. Multiple free gift lines — keep first, remove the rest.
//   2. Free gift line with quantity > 1 — clamp to 1 (Shopify merges repeated
//      raw /cart/add.js POSTs of the same variant+properties into one line
//      with stacked quantity, so a duplicate-line check alone misses this).
//   3. Free gift line below threshold — remove gift.
//   4. Orphaned gift-option / gift-box lines — remove.
//
// Self-throttle: a `validating` flag prevents re-entry while our own
// remediation cart-updates fire fresh `cart:changed` events, and a coalescing
// timer collapses bursts (e.g. batch updates) into a single run.
// ═══════════════════════════════════════════════════════════════════════════════

let cartValidating = false;
let cartValidatePending = false;
let cartValidateTimer = null;
// Set when the cart:changed listener observed a mutation originating outside
// ajax-cart.js (DevTools, third-party app, second tab, /cart/clear.js, etc).
// The validator clears it; if no violations were remediated, it also drives a
// section refresh so the on-page UI reflects the external change.
let externalCartChangePending = false;

/**
 * Find duplicate free-gift lines beyond the first one.
 * @param {Array} items - Cart items
 * @returns {Array} Items to remove (all duplicates beyond index 0)
 */
function findDuplicateFreeGifts(items) {
  const giftItems = items.filter(
    item => item.properties && item.properties._is_free_gift === 'true'
  );
  return giftItems.length > 1 ? giftItems.slice(1) : [];
}

/**
 * Find orphaned gift-option and gift-box lines (parent product no longer in cart).
 * @param {Array} items - Cart items
 * @returns {Array} Orphaned items to remove
 */
function findOrphanedGiftItems(items) {
  const regularItems = items.filter(item => {
    if (!item.properties) return true;
    return item.properties._is_gift_option !== 'true' && !item.properties._gift_box_type;
  });
  const productVariantIds = new Set(regularItems.map(item => String(item.variant_id)));
  const productSkus = new Set(regularItems.map(item => item.sku).filter(Boolean));

  return items.filter(item => {
    if (!item.properties) return false;
    if (item.properties._is_gift_option === 'true') {
      return !productVariantIds.has(item.properties._for_variant);
    }
    if (item.properties._gift_box_type) {
      const v1InCart = item.properties._for_product_1 && productSkus.has(item.properties._for_product_1);
      const v2InCart = item.properties._for_product_2 && productSkus.has(item.properties._for_product_2);
      return !v1InCart && !v2InCart;
    }
    return false;
  });
}

/**
 * Validate cart integrity end-to-end. Idempotent and self-coalescing.
 * Triggered by the cart observer on every successful cart mutation.
 * @returns {Promise<void>}
 */
async function validateCartIntegrity() {
  if (cartValidating) {
    // Another validation is in flight — schedule one more pass after it finishes
    // so we can catch state changes triggered by the current pass's own writes.
    cartValidatePending = true;
    return;
  }

  cartValidating = true;
  try {
    const cart = await cartState.get(true);
    const updates = {};
    const reasons = [];

    // ── Violation 1: duplicate free gift lines ──
    // Multiple line items with the property set (rare — Shopify normally merges
    // identical adds — but possible across distinct variants or property sets).
    const duplicateGifts = findDuplicateFreeGifts(cart.items);
    duplicateGifts.forEach(item => {
      updates[item.key] = 0;
    });
    if (duplicateGifts.length > 0) {
      reasons.push(`removed ${duplicateGifts.length} duplicate free gift line(s)`);
    }

    // ── Violation 2: free gift line with quantity > 1 ──
    // Raw /cart/add.js POSTs of the same variant + properties are merged by
    // Shopify into one line with stacked quantity, bypassing the duplicate-
    // line check above. Clamp the surviving gift line back to qty 1.
    const allGiftLines = cart.items.filter(
      item => item.properties && item.properties._is_free_gift === 'true'
    );
    const survivingGift = allGiftLines.find(item => updates[item.key] !== 0);
    if (survivingGift && survivingGift.quantity > 1) {
      updates[survivingGift.key] = 1;
      reasons.push(`clamped free gift quantity ${survivingGift.quantity} → 1`);
    }

    // ── Violation 3: free gift below threshold ──
    // Recompute qualifying assuming the surviving gift will be qty 1, not
    // whatever inflated quantity it had. Use the line's per-unit price.
    const { existingGift } = computeFreeGiftQualifyingTotal(cart);
    const threshold = getFreeGiftThreshold();
    if (existingGift && updates[existingGift.key] !== 0) {
      // Back out the entire gift line's discounted total from cart.total_price
      // to isolate what the rest of the cart actually contributes. Works
      // regardless of whether Shopify has applied a free-gift discount.
      let qualifyingCents = Number(cart.total_price) || 0;
      qualifyingCents -= Number(existingGift.final_line_price) || 0;
      if (qualifyingCents < 0) qualifyingCents = 0;
      const qualifyingTotalNow = qualifyingCents / 100;

      if (qualifyingTotalNow < threshold) {
        updates[existingGift.key] = 0;
        reasons.push(`removed ineligible free gift (qualifying ${qualifyingTotalNow} < ${threshold})`);
      }
    }

    // ── Violation 4: orphaned gift options / gift boxes ──
    const orphans = findOrphanedGiftItems(cart.items);
    orphans.forEach(item => {
      updates[item.key] = 0;
    });
    if (orphans.length > 0) {
      reasons.push(`removed ${orphans.length} orphaned gift line(s)`);
    }

    if (Object.keys(updates).length > 0) {
      console.log('Cart integrity violations:', reasons.join('; '));
      await apiLayer.cartUpdate(updates);
      cartState.invalidate();
      await uiLayer.refreshSections(['quick-cart', 'cart', 'header']);
      // The remediation write already refreshed the UI — clear the external
      // flag so we don't refresh twice.
      externalCartChangePending = false;
    } else if (externalCartChangePending) {
      // No violations, but the mutation came from outside ajax-cart.js
      // (DevTools, third-party app, /cart/clear.js, second tab). Our own
      // operations refresh sections themselves; external ones don't, so we
      // do it here to keep the on-page UI in sync.
      console.log('External cart mutation — refreshing UI sections');
      cartState.invalidate();
      await uiLayer.refreshSections(['quick-cart', 'cart', 'header']);
      externalCartChangePending = false;
    }
  } catch (error) {
    console.error('Cart integrity validation failed:', error);
  } finally {
    cartValidating = false;
    if (cartValidatePending) {
      cartValidatePending = false;
      // Run another pass on next tick to catch state changes from our own writes.
      setTimeout(validateCartIntegrity, 0);
    }
  }
}

/**
 * Schedule a coalesced cart integrity check. Multiple cart:changed events
 * within the debounce window collapse into a single validation run.
 */
function scheduleCartValidation() {
  if (cartValidateTimer) clearTimeout(cartValidateTimer);
  cartValidateTimer = setTimeout(() => {
    cartValidateTimer = null;
    validateCartIntegrity();
  }, 250);
}

// Listen for the universal cart:changed event emitted by cart-observer.js.
// IMPORTANT: read the trusted-write depth synchronously here — it's decremented
// in the apiLayer's `finally` block which runs *after* this event dispatch but
// *before* any setTimeout would fire, so we must capture origin at event time
// rather than inside the debounced validator.
document.addEventListener('cart:changed', () => {
  if (!cartTrust.isTrusted()) {
    externalCartChangePending = true;
  }
  scheduleCartValidation();
});

// ═══════════════════════════════════════════════════════════════════════════════
// SLIDER INITIALIZERS (must be globally available — section refresh strips
// inline <script> tags, and the snippet may not be in the initial server render)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Initialize Swiper for the free gift (Discovery Set) picker slider.
 * Idempotent — safe to call after every section refresh.
 */
function initCartFreeGiftSliders() {
  if (typeof Swiper === 'undefined') return;
  document.querySelectorAll('.cart-free-gift-swiper').forEach(el => {
    if (el.swiper) return;
    const wrapper = el.closest('.cart-free-gift-slider-wrapper');
    const nextEl = wrapper?.querySelector('.cart-free-gift-next') || null;
    const prevEl = wrapper?.querySelector('.cart-free-gift-prev') || null;
    new Swiper(el, {
      slidesPerView: 2.5,
      spaceBetween: 8,
      watchOverflow: true,
      centerInsufficientSlides: true,
      observer: true,
      observeParents: true,
      navigation: nextEl && prevEl
        ? { nextEl, prevEl, disabledClass: 'swiper-button-disabled' }
        : false,
      breakpoints: {
        640: { slidesPerView: 2.8, spaceBetween: 10 },
        768: { slidesPerView: 3.2, spaceBetween: 10 },
        1024: { slidesPerView: 2.5, spaceBetween: 12 },
        1280: { slidesPerView: 2.5, spaceBetween: 12 }
      }
    });
  });
}

window.initCartFreeGiftSliders = initCartFreeGiftSliders;

// ═══════════════════════════════════════════════════════════════════════════════
// UI LAYER - DOM Updates & User Feedback
// ═══════════════════════════════════════════════════════════════════════════════

const uiLayer = {
  // Track active loading states
  _activeLoadingStates: new Set(),

  /**
   * Refresh multiple sections in parallel
   * @param {string[]} sectionNames - Section names ('quick-cart', 'cart', 'header')
   * @returns {Promise<void>}
   */
  async refreshSections(sectionNames) {
    try {
      // Determine which sections to fetch based on context
      const sectionsToFetch = new Set();

      sectionNames.forEach(name => {
        if (name === 'quick-cart') {
          sectionsToFetch.add('cart-quick-cart');
        }
        if (name === 'cart' && window.location.pathname.includes('/cart')) {
          sectionsToFetch.add('cart');
        }
        if (name === 'header') {
          sectionsToFetch.add('header');
        }
      });

      if (sectionsToFetch.size === 0) {
        console.warn('No sections to refresh (sectionsToFetch is empty)');
        return;
      }

      // Fetch ALL sections in parallel (1 round-trip instead of 3!)
      console.log('🔄 Refreshing sections in parallel:', Array.from(sectionsToFetch));
      const htmlMap = await apiLayer.sectionRender(Array.from(sectionsToFetch));
      console.log('✅ Sections fetched, updating DOM...');

      // Parse and update DOM for each section
      const parser = new DOMParser();
      let updatedCount = 0;

      // Update quick cart drawer
      if (htmlMap['cart-quick-cart']) {
        const doc = parser.parseFromString(htmlMap['cart-quick-cart'], 'text/html');
        const newCartContent = doc.querySelector('#quick-cart-drawer-content');
        const currentCartContent = document.querySelector('#quick-cart-drawer-content');

        if (currentCartContent && newCartContent) {
          currentCartContent.innerHTML = newCartContent.innerHTML;
          console.log('✅ Updated quick cart drawer');
          updatedCount++;
        } else {
          console.warn('⚠️ Could not find quick cart drawer elements', {
            currentExists: !!currentCartContent,
            newExists: !!newCartContent
          });
        }
      }

      // Update main cart page
      if (htmlMap['cart']) {
        const doc = parser.parseFromString(htmlMap['cart'], 'text/html');
        const newCartContent = doc.querySelector('#cart-content');
        const currentCartContent = document.querySelector('#cart-content');

        if (currentCartContent && newCartContent) {
          currentCartContent.innerHTML = newCartContent.innerHTML;
          console.log('✅ Updated main cart page');
          updatedCount++;
        } else {
          console.warn('⚠️ Could not find main cart elements', {
            currentExists: !!currentCartContent,
            newExists: !!newCartContent
          });
        }
      }

      // Update header cart count
      if (htmlMap['header']) {
        const doc = parser.parseFromString(htmlMap['header'], 'text/html');
        const newCartIconWrapper = doc.querySelector('#cart-icon-with-numbers-wrapper');
        const currentCartIconWrapper = document.querySelector('#cart-icon-with-numbers-wrapper');

        if (currentCartIconWrapper && newCartIconWrapper) {
          currentCartIconWrapper.innerHTML = newCartIconWrapper.innerHTML;
          console.log('✅ Updated header cart count');
          updatedCount++;
        } else {
          console.warn('⚠️ Could not find header cart elements', {
            currentExists: !!currentCartIconWrapper,
            newExists: !!newCartIconWrapper
          });
        }
      }

      // Re-apply any active loading states (they were lost during innerHTML replacement)
      this._reapplyLoadingStates();

      // Re-initialize product sliders if on cart page
      if (htmlMap['cart'] && typeof window.init_product_slider === 'function') {
        window.init_product_slider();
        console.log('✅ Re-initialized product sliders');
      }

      // Re-initialize free gift (Discovery Set) sliders — script tags inside
      // replaced innerHTML don't re-execute, so call the global init explicitly.
      if ((htmlMap['cart-quick-cart'] || htmlMap['cart']) && typeof window.initCartFreeGiftSliders === 'function') {
        window.initCartFreeGiftSliders();
        console.log('✅ Re-initialized free gift sliders');
      }

      console.log(`✅ Sections refresh complete (${updatedCount} sections updated)`);
    } catch (error) {
      console.error('❌ Section refresh failed:', error);
      throw error;
    }
  },

  /**
   * Show loading state for specific context
   * @param {string} context - Context identifier (e.g., 'item-123456')
   */
  showLoading(context) {
    // Track this loading state
    this._activeLoadingStates.add(context);

    const loadingEls = document.querySelectorAll(`[data-loading="${context}"]`);
    loadingEls.forEach(el => {
      el.style.display = 'block';
    });

    // Also find legacy .item-loading elements
    if (context.startsWith('item-')) {
      const lineItemKey = context.replace('item-', '');
      const legacyLoadingEls = document.querySelectorAll(`.item-loading[data-item-key="${lineItemKey}"]`);
      legacyLoadingEls.forEach(el => {
        el.style.display = 'block';
      });
    }
  },

  /**
   * Hide loading state for specific context
   * @param {string} context - Context identifier
   */
  hideLoading(context) {
    // Remove from active loading states
    this._activeLoadingStates.delete(context);

    const loadingEls = document.querySelectorAll(`[data-loading="${context}"]`);
    loadingEls.forEach(el => {
      el.style.display = 'none';
    });

    // Also find legacy .item-loading elements
    if (context.startsWith('item-')) {
      const lineItemKey = context.replace('item-', '');
      const legacyLoadingEls = document.querySelectorAll(`.item-loading[data-item-key="${lineItemKey}"]`);
      legacyLoadingEls.forEach(el => {
        el.style.display = 'none';
      });
    }
  },

  /**
   * Re-apply active loading states after DOM update
   */
  _reapplyLoadingStates() {
    this._activeLoadingStates.forEach(context => {
      const loadingEls = document.querySelectorAll(`[data-loading="${context}"]`);
      loadingEls.forEach(el => {
        el.style.display = 'block';
      });

      // Also find legacy .item-loading elements
      if (context.startsWith('item-')) {
        const lineItemKey = context.replace('item-', '');
        const legacyLoadingEls = document.querySelectorAll(`.item-loading[data-item-key="${lineItemKey}"]`);
        legacyLoadingEls.forEach(el => {
          el.style.display = 'block';
        });
      }
    });
  },

  /**
   * Show error message for specific context
   * @param {string} message - Error message
   * @param {string} context - Context identifier (default: 'global')
   */
  showError(message, context = 'global') {
    if (context === 'global') {
      alert(message);
      return;
    }

    const errorEls = document.querySelectorAll(`[data-error="${context}"]`);
    errorEls.forEach(el => {
      el.textContent = message;
      el.style.display = 'block';
    });

    // Also find legacy .item-error elements
    if (context.startsWith('item-')) {
      const lineItemKey = context.replace('item-', '');
      const legacyErrorEls = document.querySelectorAll(`.item-error[data-item-key="${lineItemKey}"]`);
      legacyErrorEls.forEach(el => {
        el.textContent = message;
        el.style.display = 'block';
      });
    }
  },

  /**
   * Open quick cart drawer after adding to cart
   */
  openQuickCart() {
    const quickCartBtn = document.querySelector('.quick-cart-btn');
    if (quickCartBtn) {
      quickCartBtn.click();
    }
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// EVENT HANDLERS - User Interactions
// ═══════════════════════════════════════════════════════════════════════════════

document.addEventListener('DOMContentLoaded', () => {

  // ────────────────────────────────────────────────────────────────────────────
  // QUANTITY CHANGE HANDLERS
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Handle quantity change (debounced)
   * Loading will appear automatically after 800ms when debounce completes
   */
  const handleQtyChange = (input) => {
    const lineItemKey = input.dataset.itemKey;
    const newQty = Math.max(1, parseInt(input.value) || 1);
    input.value = newQty;

    // Get variant ID for gift sync
    const itemWrapper = input.closest('[data-variant-id]');
    const variantId = itemWrapper?.dataset.variantId;

    // Call debounced operation
    // User can click +/- multiple times, loading will show when debounce completes
    updateQuantity(lineItemKey, newQty, variantId);
  };

  // Quantity input change (debounced automatically by updateQuantity)
  document.addEventListener('input', (e) => {
    const input = e.target.closest('.product-qty-input');
    if (input) handleQtyChange(input);
  });

  // ────────────────────────────────────────────────────────────────────────────
  // CLICK HANDLERS (Plus/Minus, Add to Cart, Remove)
  // ────────────────────────────────────────────────────────────────────────────

  document.addEventListener('click', async (e) => {
    const plusBtn = e.target.closest('.qty-plus-btn');
    const minusBtn = e.target.closest('.qty-minus-btn');
    const addToCartBtn = e.target.closest('.add-to-cart-btn');
    const removeBtn = e.target.closest('.remote-cart-item-btn');

    // ──── Quantity +/- ────
    if (plusBtn || minusBtn) {
      e.preventDefault();
      const btn = plusBtn || minusBtn;
      const lineItemKey = btn.dataset.itemKey;
      const inputs = document.querySelectorAll(`.product-qty-input[data-item-key="${lineItemKey}"]`);

      if (!inputs.length) return;

      const current = parseInt(inputs[0].value) || 1;
      const newValue = plusBtn ? current + 1 : Math.max(1, current - 1);

      inputs.forEach(input => {
        input.value = newValue;
      });

      handleQtyChange(inputs[0]);
    }

    // ──── Add to Cart ────
    if (addToCartBtn) {
      e.preventDefault();
      const variantId = addToCartBtn.getAttribute('data-variantId');
      const quantity = parseInt(addToCartBtn.dataset.quantity) || 1;

      try {
        await addToCart(variantId, quantity);
        await uiLayer.refreshSections(['quick-cart', 'header']);
        uiLayer.openQuickCart();

        const translations = document.getElementById('theme-translations');
        addToCartBtn.innerText = translations?.dataset.addToCart || 'Add to Cart';
        addToCartBtn.disabled = false;
      } catch (error) {
        // Error already shown by addToCart
      }
    }

    // ──── Remove Item ────
    if (removeBtn) {
      e.preventDefault();
      const lineItemKey = removeBtn.dataset.itemKey;

      // Get variant ID to remove associated gift items
      const itemWrapper = removeBtn.closest('[data-variant-id]');
      const variantId = itemWrapper?.dataset.variantId;

      try {
        await removeCartItem(lineItemKey, variantId);
      } catch (error) {
        uiLayer.showError(error.message || 'Failed to remove item');
      }
    }
  });

  // ────────────────────────────────────────────────────────────────────────────
  // INITIALIZATION
  // ────────────────────────────────────────────────────────────────────────────

  // Initialize free gift slider if present in initial server render
  initCartFreeGiftSliders();

  // Run a full cart integrity validation on every page load. Catches state
  // mutated outside this tab (Vajro mobile app, second browser tab,
  // /discount/<code> URL applied between visits, abandoned-cart recovery,
  // direct app-to-cart writes). Handles duplicate free gifts, ineligible free
  // gifts, and orphaned gift options/boxes in a single pass.
  validateCartIntegrity();
});

// ═══════════════════════════════════════════════════════════════════════════════
// DEBUG UTILITIES (for development)
// ═══════════════════════════════════════════════════════════════════════════════

if (window.location.hostname === 'localhost' || window.location.search.includes('debug=cart')) {
  window.cartDebug = {
    getState: () => cartState.cart,
    invalidateCache: () => cartState.invalidate(),
    getQueueSize: () => requestQueue.queue.length,
    apiLayer,
    cartState,
    requestQueue
  };

  console.log('🛒 Cart Debug Mode Enabled. Use window.cartDebug to inspect cart state.');
}
